'use client';

import React, {
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from 'styled-components';

import { ImageComponent } from '../Image';
import { ButtonClose } from '@/components/buttons';
import { useScrollBlock } from '@/hooks/scroll';

import {
  ScModalSelect,
  ScModalSelectBody,
  ScModalSelectBodyProps,
  ScModalSelectFooter,
  ScModalSelectHeader,
  ScModalSelectOption,
  ScModalSelectProps,
  ScModalSelectRow,
  ScSelectSearchMob,
} from './styled';

type Option = {
  icon?: string;
  name: React.ReactNode;
  nameS?: string;
  nameV?: string;
  value: string | number;
  disabled?: boolean;
  isActive?: boolean;
};

interface ModalSelectProps extends ScModalSelectProps, ScModalSelectBodyProps {
  title: string;
  icon?: JSX.Element;
  options: Omit<Option, 'isActive'>[];
  borderRoundIconOption?: boolean;
  onClose: () => void;
  onChange?: (value: Option['value']) => void;
  search?: boolean;
  cols?: number;
  rowGap?: number;
  openOptionsInPortal?: boolean;
  addons?: JSX.Element;
  isPortal?: boolean;
  activeOption?: Option;
}

export const ModalSelect: FC<ModalSelectProps> = ({
  options,
  borderRoundIconOption = false,
  onChange,
  search,
  cols = 2,
  rowGap = 8,
  addons,
  isPortal,
  activeOption,
  ...props
}) => {
  const [searchValue, setSearchValue] = useState('');

  const handleChangeSearchValue = useCallback(
    (value: string) => {
      setSearchValue(value);
    },
    [setSearchValue]
  );

  const filteredOptions = useMemo(() => {
    if (searchValue === '' || search == null) return options;

    return options.filter(
      ({ nameS }) =>
        !nameS || nameS.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [searchValue, options, search]);

  const component = useMemo(() => {
    return (
      <ModalSelectContainer
        onChangeSearch={handleChangeSearchValue}
        search={search}
        {...props}
      >
        {filteredOptions.length === 0 && (
          <div className="not-found">Not found</div>
        )}
        <ScModalSelectRow cols={cols} rowGap={rowGap}>
          {filteredOptions.map((option, index) => (
            <OptionItem
              key={index}
              {...option}
              borderRoundIconOption={borderRoundIconOption}
              onChange={onChange}
              isActive={activeOption?.value === option.value}
            />
          ))}
        </ScModalSelectRow>
        {addons}
      </ModalSelectContainer>
    );
  }, [
    handleChangeSearchValue,
    search,
    props,
    filteredOptions,
    cols,
    rowGap,
    addons,
    borderRoundIconOption,
    onChange,
    activeOption?.value,
  ]);

  return isPortal ? createPortal(component, document.body) : component;
};

interface ModalSelectContainerProps
  extends PropsWithChildren,
    ScModalSelectProps,
    ScModalSelectBodyProps {
  title: string;
  icon?: JSX.Element;
  search?: boolean;
  bodyStyle?: React.CSSProperties;
  addons?: JSX.Element;
  hideCrossClose?: boolean;
  onClose: () => void;
  onChangeSearch?: (value: string) => void;
  openOptionsInPortal?: boolean;
}

export const ModalSelectContainer: FC<ModalSelectContainerProps> = ({
  children,
  title,
  icon,
  search,
  addons,
  onClose,
  onChangeSearch,
  innerAlignBottom,
  bodyStyle,
  openOptionsInPortal,
  hideCrossClose,
  isEstonia,
  ...props
}) => {
  useScrollBlock(props?.isHidden);

  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current == null || search == null) return;

    ref.current.focus();
  }, [search]);

  const handleChangeSearchValue = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChangeSearch && onChangeSearch(e.target.value);
    },
    [onChangeSearch]
  );

  const modalSelectComponent = useMemo(
    () => (
      <ScModalSelect {...props}>
        {!hideCrossClose && (
          <ButtonClose
            top={18}
            right={16}
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
          />
        )}
        <ScModalSelectHeader>
          <div className="header">
            {icon && <div className="icon">{icon}</div>}
            <div className="title">{title}</div>
          </div>
        </ScModalSelectHeader>
        {search && (
          <ScSelectSearchMob>
            <input
              ref={ref}
              type="text"
              placeholder="Search..."
              onChange={handleChangeSearchValue}
            />
          </ScSelectSearchMob>
        )}
        <ScModalSelectBody
          innerAlignBottom={innerAlignBottom}
          style={bodyStyle}
          isEstonia={isEstonia}
        >
          {children}
        </ScModalSelectBody>
        {addons && (
          <ScModalSelectFooter innerAlignBottom={innerAlignBottom}>
            {addons}
          </ScModalSelectFooter>
        )}
      </ScModalSelect>
    ),
    [
      props,
      icon,
      title,
      search,
      handleChangeSearchValue,
      innerAlignBottom,
      bodyStyle,
      isEstonia,
      children,
      addons,
      onClose,
      hideCrossClose,
    ]
  );

  return openOptionsInPortal
    ? createPortal(modalSelectComponent, document.body)
    : modalSelectComponent;
};

const OptionItem: FC<
  Pick<ModalSelectProps, 'borderRoundIconOption' | 'onChange'> & Option
> = ({ name, value, icon, borderRoundIconOption, onChange, isActive }) => {
  const { images } = useTheme();

  const handleClick = () => {
    if (onChange == null) return;
    onChange(value);
  };

  return (
    <ScModalSelectOption
      roundIcon={borderRoundIconOption}
      onClick={handleClick}
      isActive={!!isActive}
    >
      {icon && (
        <div className="icon">
          <ImageComponent src={icon} width={22} height={22} alt={`${value}`} />
        </div>
      )}
      <div className="name">{name}</div>
      {isActive && <images.tickCircleIcon.component />}
    </ScModalSelectOption>
  );
};
